/**
 * 10. User Profile & Preferences — profile fields, locale, timezone,
 * appearance, password, MFA, and session management.
 *
 * Arabic and English (C7). Each locale in the picker is named in its own
 * language ("العربية (مصر)", "English (United States)", languageName) and
 * marked with its `lang`, the convention for a language picker, as on the
 * workspace form; time zones are their IANA names, isolated left to right.
 * "Not chosen" names the language the interface is in without a saved
 * choice: the device's, or English (lib/i18n/README.md).
 *
 * Security (UI-HONEST; both buttons had no handler): two-step sign-in is
 * turned on and off in TwoStepSetup, and Revoke ends one of the person's
 * other sessions through `POST /v1/security/sessions/:id/revoke`, confirmed
 * first. The current session is not offered (ending it is Sign out).
 *
 * PROFILE-UI: the MFA card states the sign-in rule GET /v1/users/me reports
 * (`mfaPolicy.requiredFor`), never a plan claim (D1044); the Device column reads
 * the browser and system out of the stored user-agent string, with the raw
 * string as its tooltip (D1045). A failed load shows the API's error and a
 * Retry instead of a skeleton that never ends.
 */
import { useState } from "react";
import {
  Card,
  PageHeader,
  Field,
  Input,
  Select,
  Button,
  Badge,
} from "@/components/ui";
import { useConfirmedAction } from "@/components/ConfirmedAction";
import { QueryError } from "@/components/QueryError";
import { useApiQuery, useApiMutation, patch, post } from "@/lib/api/hooks";
import { usePersonLocale, useTranslation, type TFunction } from "@/lib/i18n";
import { describeUserAgent } from "@/lib/user-agent";
import { formatDateTime } from "@/lib/money";
import {
  LANGUAGE_OPTIONS,
  TIMEZONE_OPTIONS,
  languageName,
} from "@/lib/reference";
import { TwoStepSetup } from "./TwoStepSetup";

interface ProfileData {
  id: string;
  email: string;
  displayName: string;
  locale: string;
  timezone: string;
  mfaEnabled: boolean;
  /** Whom sign-in requires a second factor of (mfa-policy.ts, D1044). */
  mfaPolicy?: { requiredFor?: unknown };
  sessions: Array<{
    id: string;
    device: string;
    ip: string;
    lastActiveAt: string;
    current: boolean;
  }>;
}

/** The values of mfaPolicy.requiredFor this screen has words for. */
const REQUIRED_FOR = ["nobody", "privileged", "everyone"] as const;
type RequiredFor = (typeof REQUIRED_FOR)[number];
const isRequiredFor = (value: unknown): value is RequiredFor =>
  (REQUIRED_FOR as readonly unknown[]).includes(value);

/**
 * The session's device in words: "Chrome on Windows", or "Unknown device"
 * when the stored user-agent string names neither (D1045).
 */
export function deviceLabel(t: TFunction, userAgent: string): string {
  const seen = describeUserAgent(userAgent);
  if (!seen) return t("profile.deviceUnknown");
  if (seen.browser && seen.os)
    return t("profile.deviceOn", { browser: seen.browser, os: seen.os });
  if (seen.os) return t("profile.deviceUnknownBrowser", { os: seen.os });
  return seen.browser ?? t("profile.deviceUnknown");
}

/**
 * Whom sign-in requires a second factor of, as GET /v1/users/me says
 * (D1044). It arrives with the rest of the profile, so it is never shown
 * before the API has answered; a value this build has no words for, or an
 * API that sends none, is said to be unreadable, never guessed.
 */
function MfaRule({ requiredFor }: { requiredFor: unknown }) {
  const { t } = useTranslation();
  return (
    <div className="atlas-muted" style={{ fontSize: 13 }}>
      {isRequiredFor(requiredFor)
        ? t(`profile.mfaRule.${requiredFor}`)
        : t("profile.mfaRule.unknown")}
    </div>
  );
}

export function ProfilePage() {
  const { t, i18n } = useTranslation();
  const { data, isLoading, error, refetch, isFetching } =
    useApiQuery<ProfileData>(["me"], "/users/me");
  const save = useApiMutation(["me"], (body: Record<string, unknown>) =>
    patch("/users/me", body),
  );
  const personLocale = usePersonLocale();
  const [notice, setNotice] = useState<string | null>(null);
  const revoke = useConfirmedAction({
    run: (session: ProfileData["sessions"][number]) =>
      post(`/security/sessions/${session.id}/revoke`, {}),
    invalidate: [["me"], ["security", "sessions"]],
    onDone: () => setNotice(t("sessionControl.revoked")),
  });

  // Choosing here is the person's explicit choice, the only thing besides the
  // sign-in switch that may turn the interface Arabic. The save stores it as
  // TenantUser.locale; the refetch of GET /v1/users/me that follows reports
  // it as personalLocale, which is what switches the interface, here and on
  // every other device the person signs in on (lib/i18n/README.md).
  const saveLocale = (locale: string) => save.mutate({ locale });

  // Only when there is nothing to show: a failed refetch after a save keeps
  // the page in place.
  if (error && !data)
    return (
      <QueryError
        error={error}
        onRetry={() => void refetch()}
        retrying={isFetching}
      />
    );
  if (isLoading || !data)
    return (
      <div
        className="atlas-skeleton"
        style={{ height: 24, marginBlockEnd: 12 }}
      />
    );

  return (
    <div>
      <PageHeader title={t("profile.title")} subtitle={t("profile.subtitle")} />
      <div className="atlas-grid atlas-grid--2">
        <Card title={t("profile.card")}>
          <Field label={t("profile.displayName")}>
            <Input
              defaultValue={data.displayName}
              onBlur={(e) =>
                void save.mutateAsync({ displayName: e.target.value })
              }
            />
          </Field>
          <Field label={t("profile.email")}>
            <Input defaultValue={data.email} disabled dir="ltr" />
          </Field>
          <Field label={t("profile.locale")} hint={t("profile.localeHint")}>
            {/* Shows the person's own choice, not the workspace's locale that
                GET /v1/users/me falls back to: with no choice the interface
                is English, and an Arabic workspace must not look chosen. */}
            <Select
              key={personLocale ?? ""}
              defaultValue={personLocale ?? ""}
              onChange={(e) => {
                if (e.target.value) saveLocale(e.target.value);
              }}
            >
              <option value="" disabled>
                {t("profile.notChosen", {
                  language: t(`common.languageNames.${i18n.language}`, {
                    defaultValue: i18n.language,
                  }),
                })}
              </option>
              {/* LANGUAGE_OPTIONS: the tags a workspace can be created with.
                  Egypt is the first market (charter); ar-EG was once missing
                  here, so an Egyptian user saved as ar-EG saw another
                  locale selected. */}
              {LANGUAGE_OPTIONS.map((tag) => (
                <option key={tag} value={tag} lang={tag}>
                  {languageName(tag)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("profile.timezone")} hint={t("profile.timezoneHint")}>
            <Select
              defaultValue={data.timezone}
              onChange={(e) =>
                void save.mutateAsync({ timezone: e.target.value })
              }
            >
              {/* Was five hardcoded zones, so anyone working outside them was
                  shown times that were simply wrong for where they are. Zone
                  names are Latin: isolated left to right (LRI…PDI) so
                  "Africa/Cairo (UTC+03:00)" keeps its parenthesis in place on
                  a right-to-left screen. */}
              {TIMEZONE_OPTIONS.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {`⁦${tz.label}⁩`}
                </option>
              ))}
            </Select>
          </Field>
        </Card>
        <div>
          <Card title={t("profile.security")} className="mb-3">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBlockEnd: 12,
              }}
            >
              <div>
                <strong>{t("profile.mfa")}</strong>
                <MfaRule requiredFor={data.mfaPolicy?.requiredFor} />
              </div>
              <Badge tone={data.mfaEnabled ? "success" : "warning"}>
                {data.mfaEnabled ? t("profile.mfaOn") : t("profile.mfaOff")}
              </Badge>
            </div>
            <TwoStepSetup
              enabled={data.mfaEnabled}
              onDone={(message) => setNotice(message)}
            />
          </Card>
          {notice && (
            <p role="status" style={{ marginBlock: "0 12px" }}>
              {notice}
            </p>
          )}
          {revoke.dialog}
          <Card title={t("profile.sessions")}>
            <table className="atlas-table">
              <thead>
                <tr>
                  <th>{t("profile.device")}</th>
                  <th>{t("profile.ip")}</th>
                  <th>{t("profile.lastActive")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.sessions.map((s) => (
                  <tr key={s.id}>
                    <td>
                      {/* The browser and system in words; the stored
                          user-agent string, which is data, on hover. */}
                      <span title={s.device}>{deviceLabel(t, s.device)}</span>
                      {s.current && (
                        <Badge tone="primary" className="ml-2">
                          {t("profile.current")}
                        </Badge>
                      )}
                    </td>
                    <td className="atlas-mono">
                      <bdi dir="ltr">{s.ip}</bdi>
                    </td>
                    <td>{formatDateTime(s.lastActiveAt)}</td>
                    <td>
                      {!s.current && (
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => {
                            setNotice(null);
                            revoke.ask(s, {
                              title: t("sessionControl.revokeTitle"),
                              body: t("sessionControl.revokeBody", {
                                device: deviceLabel(t, s.device),
                              }),
                              confirmLabel: t("sessionControl.revokeConfirm"),
                            });
                          }}
                        >
                          {t("profile.revoke")}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      </div>
    </div>
  );
}
